import { useState, useEffect } from 'react';
import { components, OptionProps } from 'react-select';
import { useReactiveVar } from '@apollo/client';

import theme from '@styles/theme';
import { didPointColorOptions } from '@lib/data';
import { getNotationMethod, getDidPointColor } from '@lib/enum';
import { onChangeEventType, CustomSelectOptionType } from '@utils/types';
import { validateDidBasicSetting } from '@utils/validation';
import { didInfoVar } from '@store/index';

import {
    ColorPalette,
    FlexBox,
    RegularFont
} from '@components/styled/StyledComponents';
import { InfoSection } from '@components/share/Info/InfoSection';
import StyledCustomSelect from '@components/styled/StyledCustomSelect';
import StyledRadio from '@components/styled/StyledRadio';
import StyledColorPicker from '@components/styled/StyledColorPicker';
import StyledButton from '@components/styled/StyledButton';
import { toast } from 'react-toastify';

// 진료중 환자 강조 색상 선택 커스텀 옵션
const CustomOption = (props: OptionProps<CustomSelectOptionType>) => {
    return (
        <components.Option {...props}>
            <ColorPalette
                $margin="0 8px 0 0"
                $width={16}
                $height={16}
                $bgColor={props.data.value as string}
            />
            {props.data.label}
        </components.Option>
    );
};

const BasicSetting = () => {
    const {
        version,
        usePatientChart,
        useCallingPati,
        notationMethod,
        didBgColor,
        didPointColor,
        useWeatherAni
    } = useReactiveVar(didInfoVar);
    const [mediaType, setMediaType] = useState<CustomSelectOptionType | null>(
        null
    );
    const [writtenType, setWrittenType] =
        useState<CustomSelectOptionType | null>(null);
    const [pointColor, setPointColor] = useState<CustomSelectOptionType | null>(
        didPointColorOptions[0]
    );
    const [inputs, setInputs] = useState({
        usePatientChart: '',
        useCallingPati: '',
        useWeatherAni: '',
        didBgColor: '#ffffff'
    });

    // 기존 DID 설정 불러오기
    useEffect(() => {
        if (version) {
            const pointColor = didPointColor.toLocaleUpperCase();
            setMediaType({ label: `${version}.0`, value: version });
            setWrittenType({
                label: getNotationMethod(notationMethod) as string,
                value: notationMethod
            });
            setPointColor({
                label: getDidPointColor(pointColor),
                value: pointColor
            });
            setInputs((prev) => {
                return {
                    ...prev,
                    usePatientChart: `${usePatientChart}`,
                    useCallingPati: `${useCallingPati}`,
                    didBgColor,
                    useWeatherAni: `${useWeatherAni}`
                };
            });
        }
    }, [
        version,
        usePatientChart,
        useCallingPati,
        notationMethod,
        didBgColor,
        didPointColor,
        useWeatherAni
    ]);

    const onChange = (e: onChangeEventType) => {
        const { name, value } = e.target;
        setInputs((prev) => {
            return {
                ...prev,
                [name]: value
            };
        });
    };
    const onSubmit = () => {
        if (!validateDidBasicSetting(inputs, writtenType)) return;
        toast.success('저장되었습니다.');
    };

    return (
        <>
            <InfoSection title="기본 설정" ROW>
                <StyledCustomSelect
                    title="헬로미디어 타입"
                    name="mediaType"
                    value={mediaType}
                    options={[
                        { label: '1.0', value: 1 },
                        { label: '2.0', value: 2 },
                        { label: '3.0', value: 3 }
                    ]}
                    onChange={setMediaType}
                    placeholder="헬로미디어 타입 선택"
                    description="타입 변경은 헬로미디어 관리자 문의 및 승인이 필요합니다."
                    width="630px"
                    margin="0 0 24px"
                    disabled
                />
                <FlexBox $margin="0 0 28px">
                    <RegularFont>대기환자 노출 섹션 사용 여/부</RegularFont>
                    <RegularFont
                        $fontColor={theme.colors.deepGrayFontColor}
                        $margin="0 0 0 4px"
                    >
                        (이지스 차트를 이용중인 고객만 사용 가능)
                    </RegularFont>
                </FlexBox>
                <FlexBox $margin="0 0 40px">
                    {[
                        { label: '사용', value: 'true' },
                        { label: '미사용', value: 'false' }
                    ].map((data, index) => (
                        <StyledRadio
                            key={`${index}-usePatientChart`}
                            name="usePatientChart"
                            radioId={`usePatientChart-${data.value}`}
                            label={data.label}
                            value={data.value}
                            checked={inputs.usePatientChart}
                            onChange={onChange}
                            margin="0 20px 0 0"
                        />
                    ))}
                </FlexBox>
                <FlexBox $margin="0 0 28px">
                    <RegularFont>환자 호출 사용 여/부</RegularFont>
                    <RegularFont
                        $fontColor={theme.colors.deepGrayFontColor}
                        $margin="0 0 0 4px"
                    >
                        (대기환자 노출 섹션 사용시 사용 가능)
                    </RegularFont>
                </FlexBox>
                <FlexBox $margin="0 0 40px">
                    {[
                        { label: '사용', value: 'true' },
                        { label: '미사용', value: 'false' }
                    ].map((data, index) => (
                        <StyledRadio
                            key={`${index}-useCallingPati`}
                            name="useCallingPati"
                            radioId={`useCallingPati-${data.value}`}
                            label={data.label}
                            value={data.value}
                            checked={inputs.useCallingPati}
                            onChange={onChange}
                            margin="0 20px 0 0"
                        />
                    ))}
                </FlexBox>
                <StyledCustomSelect
                    title="진료실명 표기 방법"
                    name="writtenType"
                    value={writtenType}
                    options={[
                        { label: '진료실명', value: 1 },
                        { label: '의사명', value: 2 }
                    ]}
                    onChange={setWrittenType}
                    placeholder="진료실명 표기 방법 선택"
                    width="630px"
                    margin="0 0 24px"
                />
                <StyledColorPicker
                    title="배경화면 색상 선택"
                    id="didBgColor"
                    name="didBgColor"
                    value={inputs.didBgColor}
                    onChange={onChange}
                    width={630}
                    margin="0 0 20px"
                />
                <StyledCustomSelect
                    title="진료중 환자 강조 색상 선택"
                    name="pointColor"
                    value={pointColor}
                    options={didPointColorOptions}
                    onChange={setPointColor}
                    customOption={CustomOption}
                    placeholder="진료중 환자 강조 색상 선택"
                    width="630px"
                    margin="0 0 24px"
                    COLOR
                />
                <RegularFont $margin="0 0 28px">
                    날씨 애니메이션 사용 여/부
                </RegularFont>
                <FlexBox $margin="0 0 76px">
                    {[
                        { label: '사용', value: 'true' },
                        { label: '미사용', value: 'false' }
                    ].map((data, index) => (
                        <StyledRadio
                            key={`${index}-useWeatherAni`}
                            name="useWeatherAni"
                            radioId={`useWeatherAni-${data.value}`}
                            label={data.label}
                            value={data.value}
                            checked={inputs.useWeatherAni}
                            onChange={onChange}
                            margin="0 20px 0 0"
                        />
                    ))}
                </FlexBox>
                <StyledButton
                    title="저장하기"
                    onClick={onSubmit}
                    width={305}
                    bgColor={theme.colors.positiveBlue}
                />
            </InfoSection>
        </>
    );
};

export default BasicSetting;
